/**
 * frontend/src/screens/OperationsTab.js
 *
 * Operations tab for the admin portal — unified view for day-to-day
 * Solidiphy operations staff. Mimics PSP portal layout but covers all
 * operational statuses and adds Meshy task status + retry.
 *
 * Statuses covered:
 *   in_process, mqc, ready_to_model, sculpting, ready_to_print, printing,
 *   finishing, qc, packing, shipped_to_solidiphy,
 *   received_accepted, received_rejected, ready_to_ship, shipped_to_customer
 *
 * Features:
 *   - Grouped by Company → Group → Order → Line Item
 *   - Photo thumbnails (front + back) with click-to-enlarge
 *   - Model PNG preview from Original/ folder
 *   - Meshy task status inline with retry modal (3 options)
 *   - Status change dropdown per line item
 *   - Original / Modeled file counts with download
 *   - Search, status filter, company filter, date range
 */

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  getAdminLineItems, updateItemStatus, qcDecisionAdmin,
  getAdminMeshyTasks, retryAdminMeshyTask,
  getPSPCompanies, assignItemsToPSP, getAdminItemPartnerData, reviewPSPModel, // Phase 12
  getAdminItemModels, // Phase 12b
  shareMeshyWithPSP,  // Phase 12d
  getAdminOrderSlips, getAdminItemShipments, getAdminSlipPrintData, // Phase 12f
} from '../api';
import PartnerData from '../components/PartnerData'; // Phase 12
import PSPModels, { timeLeft } from '../components/PSPModels'; // Phase 12b
import { printPackingSlip, openSlipWindow } from '../utils/packingSlip'; // Phase 12e

// Phase 12f — packing slips are created automatically when the PSP ships
// (one per shipment group). Operations only views them.
async function viewSlip(slipId, toast) {
  const win = openSlipWindow(); // open now (click handler) so pop-up blockers allow it
  try {
    const { data } = await getAdminSlipPrintData(slipId);
    printPackingSlip(data.items, data.slipId, data.createdAt, { orderInfo: data.orderInfo, win, autoPrint: false });
  } catch (err) {
    if (win) win.close();
    toast(err.response?.data?.error || 'Could not open packing slip', 'error');
  }
}

// Item panel: every shipment of this item to Solidiphy (Phase 12f) + receipt note
function ItemShipments({ item, toast }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { getAdminItemShipments(item.id).then(r => setRows(r.data)).catch(() => setRows([])); }, [item.id, item.shipmentCount]);
  if (!rows || (!rows.length && !item.returnReason)) return null;
  return (
    <div style={{ marginTop:12, border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#fff' }}>
      <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Shipments to Solidiphy</div>
      {item.returnReason && (
        <div style={{ fontSize:12, color:'#991B1B', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6, padding:'6px 8px', marginBottom:8 }}>
          Rejected on receipt{item.returnedAt ? ` (${new Date(item.returnedAt).toLocaleDateString()})` : ''}: {item.returnReason}
        </div>
      )}
      {rows.map((r, i) => (
        <div key={r.slipId} style={{ display:'flex', gap:10, alignItems:'center', fontSize:12, padding:'3px 0' }}>
          <span style={{ color:'#9ca3af', width:18 }}>{i + 1}.</span>
          <strong style={{ fontFamily:'monospace' }}>{r.slipId}</strong>
          <span style={{ color:'#374151' }}>{r.shipmentGroupId ? `Group ${r.shipmentGroupId}` : 'Portal slip'}</span>
          <span style={{ color:'#9ca3af' }}>{new Date(r.createdAt).toLocaleDateString()}</span>
          <button onClick={() => viewSlip(r.slipId, toast)}
            style={{ fontSize:11, padding:'1px 8px', borderRadius:6, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer' }}>View</button>
        </div>
      ))}
    </div>
  );
}

// Order row: View slip — one slip opens directly, several show a small picker
function OrderSlipsButton({ orderId, toast }) {
  const [slips, setSlips] = useState(null);
  const [open, setOpen]   = useState(false);
  const click = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    try {
      const { data } = await getAdminOrderSlips(orderId);
      if (!data.length) { toast('No packing slip for this order yet', 'error'); return; }
      if (data.length === 1) { viewSlip(data[0].slipId, toast); return; }
      setSlips(data); setOpen(true);
    } catch (err) { toast('Could not load packing slips', 'error'); }
  };
  return (
    <span style={{ position:'relative', flexShrink:0 }} onClick={e => e.stopPropagation()}>
      <button onClick={click} title="View packing slip"
        style={{ fontSize:11, padding:'2px 8px', borderRadius:6, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer' }}>
        📄 View slip
      </button>
      {open && slips && (
        <div style={{ position:'absolute', right:0, top:'110%', zIndex:20, background:'#fff', border:'1px solid #d1d5db', borderRadius:8, boxShadow:'0 4px 12px rgba(0,0,0,0.12)', minWidth:230, padding:4 }}>
          {slips.map(sl => (
            <div key={sl.slipId} onClick={() => { setOpen(false); viewSlip(sl.slipId, toast); }}
              style={{ padding:'6px 10px', fontSize:12, cursor:'pointer', borderRadius:6 }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
              <strong style={{ fontFamily:'monospace' }}>{sl.slipId}</strong>
              {sl.shipmentGroupId && <span style={{ color:'#6b7280' }}> · {sl.shipmentGroupId}</span>}
              <span style={{ color:'#9ca3af' }}> · {new Date(sl.createdAt).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

// Phase 12b — sculpting stage labels (PSP-reported)
const STAGE_LABEL = {
  generating: 'Generating RAW', raw_generated: 'RAW generated', raw_accepted: 'RAW accepted',
  cleaning: 'Cleaning', quality_approved: 'Quality approved', awaiting_payment: 'Awaiting payment',
};
import { Badge, RushBadge, StatusMultiSelect, LazyImage, Spinner, useToast, Toasts } from '../components/UI';

const API_BASE = process.env.REACT_APP_API_URL || '';
// Use relative path for image src to go through CRA proxy and avoid CORS
const IMG_BASE = '';

// ── Status config ─────────────────────────────────────────────────────────────
const OPS_STATUSES = [
  'in_process','mqc','ready_to_model','sculpting','ready_to_print','printing','finishing','qc','packing',
  'shipped_to_solidiphy','received_accepted','received_rejected','ready_to_ship','shipped_to_customer',
];

const STATUS_LABELS = {
  in_process:           'In Process',
  mqc:                  'Quality Check',
  ready_to_model:       'Ready to Model',
  sculpting:            'Sculpting',
  ready_to_print:       'Ready to Print',
  printing:             'Printing',
  finishing:            'Finishing',
  qc:                   'QC',
  packing:              'Packing',
  shipped_to_solidiphy: 'Shipped to Solidiphy',
  received_accepted:    'Received — Accepted',
  received_rejected:    'Received — Rejected',
  ready_to_ship:        'Ready to Ship',
  shipped_to_customer:  'Shipped to Customer',
};

// Valid transitions per status — what can ops change to.
// The 7-stage PSP production run (ready_to_model…packing) allows free movement
// in any direction, matching the PSP portal. QC has no dropdown transitions —
// it's handled by the dedicated Pass/Fail buttons instead (see OpsLineItemRow).
const PSP_MID_STATUSES = ['ready_to_model','sculpting','ready_to_print','printing','finishing','qc','packing'];
const OPS_TRANSITIONS = {
  in_process:           ['mqc','ready_to_model'],
  mqc:                  ['in_process','ready_to_model'],
  ready_to_model:       [...PSP_MID_STATUSES.filter(s => s !== 'ready_to_model'), 'mqc'],
  sculpting:            PSP_MID_STATUSES.filter(s => s !== 'sculpting'),
  ready_to_print:       PSP_MID_STATUSES.filter(s => s !== 'ready_to_print'),
  printing:             PSP_MID_STATUSES.filter(s => s !== 'printing'),
  finishing:            PSP_MID_STATUSES.filter(s => s !== 'finishing'),
  qc:                   [], // Pass/Fail buttons instead
  packing:              [...PSP_MID_STATUSES.filter(s => s !== 'packing'), 'shipped_to_solidiphy'],
  shipped_to_solidiphy: ['received_accepted','received_rejected'],
  received_accepted:    ['ready_to_ship','packing'],
  received_rejected:    ['packing'],
  ready_to_ship:        ['shipped_to_customer'],
  shipped_to_customer:  [],
};

// QC fail reason → auto-target status (mirrors backend QC_FAIL_TARGET)
const QC_FAIL_REASONS = ['Print Issue', 'Post Processing Issue', 'Digital Issue'];
// Mirrors backend QC_FAIL_TARGET — used for optimistic UI updates only; the
// server is still the source of truth and recomputes this independently.
const QC_FAIL_TARGET = {
  'Print Issue':           'printing',
  'Post Processing Issue': 'printing',
  'Digital Issue':         'sculpting',
};

const MESHY_STATUS_LABELS = {
  queued:      { label: 'Queued',      color: '#6B7280' },
  submitted:   { label: 'Generating',  color: '#2563EB' },
  analyzing:   { label: 'Analyzing',   color: '#7C3AED' },
  resizing:    { label: 'Resizing',    color: '#0891B2' },
  retexturing: { label: 'Retexturing', color: '#DB2777' },
  succeeded:   { label: 'Succeeded',   color: '#059669' },
  failed:      { label: 'Failed',      color: '#DC2626' },
};

// Depth-based background shading (matches PSP portal)
const depthBg = (d) => ['#f0f4f8','#f7f9fb','#fbfcfd','#ffffff'][Math.min(d, 3)];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

// ── Image lightbox ────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, cursor:'zoom-out' }}>
      <img src={src} alt="Preview" style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:8 }} onClick={e => e.stopPropagation()} />
      <button onClick={onClose} style={{ position:'absolute', top:20, right:24, background:'none', border:'none', color:'#fff', fontSize:28, cursor:'pointer' }}>✕</button>
    </div>
  );
}

// ── Meshy Retry Modal ─────────────────────────────────────────────────────────
function MeshyRetryModal({ task, onClose, onSuccess, toast }) {
  const [settings, setSettings] = useState({
    imageEnhancement: task?.settingImageEnhancement || false,
    removeLighting:   task?.settingRemoveLighting   || false,
    poseMode:         task?.settingPoseMode          || '',
    useBackPhoto:     task?.settingUseBackPhoto      !== false,
    figureSize:       task?.settingFigureSize        || 0,
    hdTexture:        task?.settingHdTexture         || false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Determine what will run based on what changed vs existing task settings
  const determineRunType = () => {
    if (!task?.meshyTaskId) return 'rerun'; // no existing model — always full rerun

    const genChanged = (
      settings.imageEnhancement !== (task.settingImageEnhancement || false) ||
      settings.poseMode         !== (task.settingPoseMode          || '')    ||
      settings.useBackPhoto     !== (task.settingUseBackPhoto      !== false)
    );
    const textureChanged = (
      settings.hdTexture      !== (task.settingHdTexture      || false) ||
      settings.removeLighting !== (task.settingRemoveLighting || false)
    );
    const sizeChanged = settings.figureSize !== (task.settingFigureSize || 0);

    if (genChanged) return 'rerun';
    if (textureChanged && sizeChanged) return 'resize+retexture';
    if (textureChanged) return 'retexture';
    if (sizeChanged)    return 'resize';
    return 'rerun'; // nothing changed — default to full rerun
  };

  const runType = determineRunType();

  const RUN_LABELS = {
    'rerun':           { label: 'Rerun',            color: '#2563EB' },
    'retexture':       { label: 'Retexture',         color: '#DB2777' },
    'resize':          { label: 'Resize',            color: '#0891B2' },
    'resize+retexture':{ label: 'Resize + Retexture',color: '#7C3AED' },
  };

  const apiType = runType === 'rerun' ? 'scratch'
    : runType === 'retexture' ? 'retexture'
    : runType === 'resize' ? 'resize'
    : 'resize+retexture';

  const submit = async () => {
    setSubmitting(true);
    try {
      await retryAdminMeshyTask(task.id, { type: apiType, ...settings });
      toast('Retry submitted', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      toast('Retry failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSubmitting(false); }
  };

  const cfg = RUN_LABELS[runType];
  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500 }}>
      <div style={{ background:'var(--surface)', borderRadius:12, padding:24, width:400, boxShadow:'0 8px 32px rgba(0,0,0,0.2)', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ fontSize:16, fontWeight:700, marginBottom:4 }}>Retry 3D Task #{task.id}</div>
        <div style={{ fontSize:12, color:'var(--text2)', marginBottom:20 }}>
          {task.order?.orderNumber} — {task.orderItem?.lineItemDescription || task.lineItemDescription}
        </div>

        {/* Generation settings */}
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Generation</div>
        <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, cursor:'pointer' }}>
          <input type='checkbox' checked={settings.imageEnhancement} onChange={e => set('imageEnhancement', e.target.checked)} />
          <span style={{ fontSize:13 }}>Image Enhancement</span>
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10,
          cursor: task.photoOneDrivePath2 ? 'pointer' : 'not-allowed', opacity: task.photoOneDrivePath2 ? 1 : 0.4 }}>
          <input type='checkbox' checked={settings.useBackPhoto} disabled={!task.photoOneDrivePath2}
            onChange={e => set('useBackPhoto', e.target.checked)} />
          <span style={{ fontSize:13 }}>Use Back Photo {!task.photoOneDrivePath2 ? '(not available)' : ''}</span>
        </label>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:13, marginBottom:6 }}>Pose</div>
          <div style={{ display:'flex', gap:8 }}>
            {['', 'a-pose', 't-pose'].map(p => (
              <button key={p} onClick={() => set('poseMode', p)}
                style={{ padding:'4px 12px', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:500,
                  background: settings.poseMode === p ? 'var(--brand)' : 'var(--admin-bg)',
                  color:      settings.poseMode === p ? '#fff' : 'var(--text)',
                  border:     `1px solid ${settings.poseMode === p ? 'var(--brand)' : 'var(--border)'}` }}>
                {p === '' ? 'None' : p}
              </button>
            ))}
          </div>
        </div>

        {/* Texture settings */}
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8, marginTop:4 }}>Texture</div>
        <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, cursor:'pointer' }}>
          <input type='checkbox' checked={settings.hdTexture} onChange={e => set('hdTexture', e.target.checked)} />
          <span style={{ fontSize:13 }}>HD Texture (4K)</span>
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, cursor:'pointer' }}>
          <input type='checkbox' checked={settings.removeLighting} onChange={e => set('removeLighting', e.target.checked)} />
          <span style={{ fontSize:13 }}>Remove Lighting</span>
        </label>

        {/* Size */}
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Figure Size</div>
        <select value={settings.figureSize} onChange={e => set('figureSize', parseInt(e.target.value))}
          style={{ padding:'6px 10px', borderRadius:6, border:'1px solid var(--border)', fontSize:13, width:'100%', marginBottom:20 }}>
          <option value={0}>SKU default</option>
          {[2,3,4,5,6,7].map(n => <option key={n} value={n}>{n}"</option>)}
        </select>

        <button onClick={() => setSettings({
          imageEnhancement: false, removeLighting: false, poseMode: '',
          useBackPhoto: true, figureSize: 0, hdTexture: false,
        })} style={{ fontSize:12, color:'var(--text2)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline', marginBottom:16, padding:0, display:'block' }}>
          Reset to defaults
        </button>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className='btn' onClick={onClose} disabled={submitting}>Cancel</button>
          <button className='btn btn-primary' onClick={submit} disabled={submitting}>
            {submitting ? 'Submitting…' : `Submit — ${cfg.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Line Item expanded panel ───────────────────────────────────────────────────
function OpsLineItemPanel({ item, toast, onRefresh, meshyTask, onRetryMeshy }) {
  const [partnerData, setPartnerData] = useState(null); // Phase 12
  const [modelsKey, setModelsKey]     = useState(0);    // Phase 12b — reload PSP models
  const [previewFiles, setPreviewFiles] = useState(null);
  const [meshyShared, setMeshyShared]   = useState(!!item.shareMeshyWithPsp); // Phase 12d
  const [sharing, setSharing]           = useState(false);
  const canShareMeshy = !!item.assignedPspCompany?.apiEnabled;
  const toggleMeshyShare = async () => {
    const next = !meshyShared;
    if (next && !window.confirm(`Share this item's Meshy model with ${item.assignedPspCompany?.name}? They'll be notified and can download it through the API.`)) return;
    setSharing(true);
    try {
      await shareMeshyWithPSP(item.id, next);
      setMeshyShared(next);
      toast(next ? `Meshy model shared with ${item.assignedPspCompany?.name}` : 'Meshy model no longer shared', 'success');
      onRefresh();
    } catch (err) { toast(err.response?.data?.error || 'Could not update sharing', 'error'); }
    finally { setSharing(false); }
  };
  const [files,       setFiles]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [lightbox,    setLightbox]    = useState(null);
  const [showViewer,  setShowViewer]  = useState(false);

  const orderId = item.orderId || item.order?.id;

  useEffect(() => {
    if (!orderId) { setLoading(false); return; }
    fetch(`${API_BASE}/api/admin/orders/${orderId}/items/${item.id}/files`, { credentials:'include' })
      .then(r => r.json())
      .then(d => setFiles(d))
      .catch(() => setFiles({ original:[], modeled:[] }))
      .finally(() => setLoading(false));
  }, [orderId, item.id]);

  const downloadUrl = (path, name) =>
    `${IMG_BASE}/api/admin/download?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(name)}`;

  // Phase 12 — PSP assignment, model review, PSP-reported data
  const loadPartnerData = useCallback(() => {
    getAdminItemPartnerData(item.id).then(r => setPartnerData(r.data)).catch(() => setPartnerData(null));
  }, [item.id]);
  useEffect(() => { loadPartnerData(); }, [loadPartnerData]);

  const loadModels = useCallback(() => getAdminItemModels(item.id), [item.id]);
  const handleReview = async (decision, reason) => {
    try {
      await reviewPSPModel(item.id, decision, reason);
      toast({ accepted: 'RAW accepted — PSP notified', rejected: 'RAW rejected — PSP notified', on_hold: 'RAW on hold — review clock stopped' }[decision], 'success');
      setModelsKey(k => k + 1);
      loadPartnerData();
      onRefresh();
    } catch (err) { toast(err.response?.data?.error || 'Review failed', 'error'); throw err; }
  };

  // GLB embeds its own texture — no standalone preview image, so there's
  // nothing to show as a static thumbnail. "View 3D" opens the real model.
  const hasModel = files?.original?.some(f => f.name.endsWith('.glb'));

  const order = item.order;
  const shippingName = `${order?.shippingFirstName || ''} ${order?.shippingLastName || ''}`.trim()
    || `${order?.customerFirstName || ''} ${order?.customerLastName || ''}`.trim();

  const meshyCfg = meshyTask ? MESHY_STATUS_LABELS[meshyTask.status] : null;

  return (
    <div style={{ padding:'12px 16px 16px', background:'#f7f9fb', borderTop:'0.5px solid #e5e7eb' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:12 }}>

        {/* Photos */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#fff' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Photos</div>
          <div style={{ display:'flex', gap:8 }}>
            {item.photoOneDrivePath && item.photoUrl !== '#demo' && (
              <LazyImage src={`${IMG_BASE}/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}`}
                alt="Front" onClick={() => setLightbox(`${IMG_BASE}/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}`)}
                style={{ width:80, height:80, objectFit:'cover', borderRadius:6, border:'1px solid #e5e7eb', cursor:'zoom-in' }} />
            )}
            {item.photoOneDrivePath2 && item.photoUrl2 !== '#demo' && (
              <LazyImage src={`${IMG_BASE}/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath2)}`}
                alt="Back" onClick={() => setLightbox(`${IMG_BASE}/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath2)}`)}
                style={{ width:80, height:80, objectFit:'cover', borderRadius:6, border:'1px solid #e5e7eb', cursor:'zoom-in' }} />
            )}
            {!item.photoOneDrivePath && <div style={{ fontSize:12, color:'#9ca3af', fontStyle:'italic' }}>No photos</div>}
          </div>
        </div>

        {/* 3D Model preview */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#fff' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>3D Model</span>
            {item.meshyWarning && <span style={{ fontSize:10, background:'#FEF3C7', color:'#92400E', padding:'1px 6px', borderRadius:8 }}>⚠️ Watertight</span>}
          </div>
          {loading ? <div style={{height:80,display:'flex',alignItems:'center'}}><Spinner size={16}/></div> : (
            <div>
              {hasModel ? (
                <div style={{ width:80, height:80, borderRadius:6, border:'1px solid #e5e7eb', background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:8, fontSize:24 }}>🧊</div>
              ) : (
                <div style={{ fontSize:12, color:'#9ca3af', fontStyle:'italic', marginBottom:8 }}>No model yet</div>
              )}
              {hasModel && (
                <button onClick={() => setShowViewer(true)}
                  style={{ fontSize:11, padding:'4px 10px', borderRadius:6, border:'1px solid var(--brand)', background:'var(--brand)', color:'#fff', cursor:'pointer', fontWeight:500 }}>
                  🔄 View 3D
                </button>
              )}
              {/* Phase 12d — share this item's Meshy model with an API PSP */}
              {hasModel && canShareMeshy && (
                <div style={{ marginTop:6 }}>
                  <button onClick={toggleMeshyShare} disabled={sharing}
                    style={{ fontSize:11, padding:'4px 10px', borderRadius:6, cursor:'pointer', fontWeight:500,
                      border: meshyShared ? '1px solid #d1d5db' : '1px solid #7C3AED',
                      background: meshyShared ? '#fff' : '#7C3AED', color: meshyShared ? '#374151' : '#fff' }}>
                    {meshyShared ? 'Stop sharing Meshy model' : 'Share Meshy model with PSP'}
                  </button>
                  {meshyShared && <div style={{ fontSize:10, color:'#6D28D9', marginTop:3 }}>Shared with {item.assignedPspCompany?.name}</div>}
                </div>
              )}
            </div>
          )}
          {/* Meshy task status */}
          {meshyTask && (
            <div style={{ marginTop:8 }}>
              <span style={{ background: (meshyCfg?.color || '#6B7280') + '22', color: meshyCfg?.color || '#6B7280',
                padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:10 }}>
                {meshyCfg?.label || meshyTask.status}
              </span>
              {['failed','succeeded'].includes(meshyTask.status) && (
                <button className='btn btn-sm' onClick={onRetryMeshy}
                  style={{ marginTop:6, fontSize:11, display:'block' }}>
                  Retry
                </button>
              )}
            </div>
          )}
        </div>

        {/* Files */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#fff' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Files</div>
          {loading ? <Spinner size={16} /> : (
            <>
              <div style={{ marginBottom:6 }}>
                <div style={{ fontSize:11, color:'#6b7280', marginBottom:4, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span>Original ({files?.original?.length || 0})</span>
                  {files?.originalWebUrl && (
                    <a href={files.originalWebUrl} target='_blank' rel='noreferrer'
                      style={{ fontSize:11, color:'var(--brand)', textDecoration:'none' }}>
                      Open folder ↗
                    </a>
                  )}
                </div>
                {files?.original?.map((f,i) => (
                  <div key={i} style={{ fontSize:11, color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:2 }}>
                    {f.name}
                  </div>
                ))}
                {!files?.original?.length && <div style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic' }}>None</div>}
              </div>
              <div>
                <div style={{ fontSize:11, color:'#6b7280', marginBottom:4, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span>Modeled ({files?.modeled?.length || 0})</span>
                  {files?.modeledWebUrl && (
                    <a href={files.modeledWebUrl} target='_blank' rel='noreferrer'
                      style={{ fontSize:11, color:'var(--brand)', textDecoration:'none' }}>
                      Open folder ↗
                    </a>
                  )}
                </div>
                {files?.modeled?.map((f,i) => (
                  <div key={i} style={{ fontSize:11, color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:2 }}>
                    {f.name}
                  </div>
                ))}
                {!files?.modeled?.length && <div style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic' }}>None</div>}
              </div>
            </>
          )}
        </div>

        {/* Shipping info */}
        <div style={{ border:'1px solid #d1d5db', borderRadius:8, padding:'10px 12px', background:'#fff' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Ship To</div>
          <div style={{ fontSize:12, color:'#374151', lineHeight:1.6 }}>
            <div style={{ fontWeight:500 }}>{shippingName || '—'}</div>
            {order?.roomNumber && <div style={{ color:'#6b7280' }}>Room: {order.roomNumber}</div>}
            {order?.shippingAddress1 && (
              <>
                <div>{order.shippingAddress1}</div>
                {order.shippingAddress2 && <div>{order.shippingAddress2}</div>}
                <div>{order.shippingCity}{order.shippingProvince ? `, ${order.shippingProvince}` : ''} {order.shippingZip}</div>
                <div>{order.shippingCountry}</div>
              </>
            )}
            {order?.customerPhone && <div style={{ color:'#6b7280', marginTop:4 }}>{order.customerPhone}</div>}
          </div>
          <div style={{ marginTop:8, padding:'6px 8px', background:'#f9fafb', borderRadius:6, border:'0.5px solid #e5e7eb', fontSize:11, fontFamily:'monospace', color:'#6b7280', lineHeight:1.7 }}>
            <div>Ref: <span style={{ color:'#374151', fontWeight:500 }}>{item.referenceNumber || '—'}</span></div>
            <div>SKU: <span style={{ color:'#374151' }}>{item.sku || '—'}</span></div>
            <div>ID: <span style={{ color:'#374151' }}>{item.lineItemDescription}</span></div>
          </div>
        </div>
      </div>

      <PSPModels load={loadModels} refreshKey={modelsKey} onDecide={handleReview}
        downloadUrl={(path, name) => downloadUrl(path, name)}
        onPreview={(v) => setPreviewFiles([{ name: v.filename, path: v.path }])} />
      <PartnerData data={partnerData} showAssignee />
      <ItemShipments item={item} toast={toast} />
      {previewFiles && ReactDOM.createPortal(
        <ModelViewer files={previewFiles} onClose={() => setPreviewFiles(null)} />, document.body)}

      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      {showViewer && ReactDOM.createPortal(
        <ModelViewer
          files={files?.original || []}
          onClose={() => setShowViewer(false)}
        />,
        document.body
      )}
    </div>
  );
}

// ── QC Fail reason modal ───────────────────────────────────────────────────────
function QCFailModal({ onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1600 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:12, padding:22, width:340, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize:15, fontWeight:700, marginBottom:14 }}>QC Fail — Reject Reason</div>
        {QC_FAIL_REASONS.map(r => (
          <label key={r} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 4px', fontSize:13, cursor:'pointer' }}>
            <input type="radio" name="qcFailReason" checked={reason === r} onChange={() => setReason(r)} />
            {r}
          </label>
        ))}
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!reason} onClick={() => onSubmit(reason)}>Submit</button>
        </div>
      </div>
    </div>
  );
}

// ── Line Item Row ─────────────────────────────────────────────────────────────
const OpsLineItemRow = React.memo(function OpsLineItemRow({ item, depth, toast, onRefresh, meshyTask, onRetryMeshy, onOptimisticUpdate, selected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const [rowLightbox, setRowLightbox] = useState(null);
  const [showQCFail, setShowQCFail] = useState(false);
  const orderId = item.orderId || item.order?.id;
  const availableStatuses = OPS_TRANSITIONS[item.itemStatus] || [];
  const isQC = item.itemStatus === 'qc';

  const handleStatusChange = async (newStatus) => {
    const prevStatus = item.itemStatus;
    // Phase 12f — rejecting on receipt needs a note (sent to the PSP, who decides where to restart)
    let note;
    if (newStatus === 'received_rejected') {
      note = window.prompt('Reason for rejecting on receipt — this note is sent to the PSP:');
      if (!note || !note.trim()) return;
    }
    const shown = newStatus === 'received_accepted' ? 'ready_to_ship' : newStatus; // accept → Ready to Ship automatically
    onOptimisticUpdate(item.id, { itemStatus: shown }); // instant UI update
    try {
      await updateItemStatus(orderId, item.id, newStatus, note);
      toast(newStatus === 'received_accepted' ? 'Received & Accepted → Ready to Ship'
        : newStatus === 'received_rejected' ? 'Rejected on receipt — PSP notified' : `Status → ${STATUS_LABELS[newStatus]}`, 'success');
      onRefresh();
    } catch (err) {
      onOptimisticUpdate(item.id, { itemStatus: prevStatus }); // roll back
      toast(err.response?.data?.error || 'Failed', 'error');
    }
  };

  const handleQC = async (result, reason) => {
    const prevStatus = item.itemStatus;
    const targetStatus = result === 'pass' ? 'packing' : QC_FAIL_TARGET[reason];
    setShowQCFail(false);
    if (targetStatus) onOptimisticUpdate(item.id, { itemStatus: targetStatus }); // instant UI update
    try {
      await qcDecisionAdmin(orderId, item.id, result, reason);
      toast(result === 'pass' ? 'QC Passed → Packing' : `QC Failed → ${reason}`, 'success');
      onRefresh();
    } catch (err) {
      onOptimisticUpdate(item.id, { itemStatus: prevStatus }); // roll back
      toast(err.response?.data?.error || 'Failed', 'error');
    }
  };

  const indent = 16 + depth * 20;
  const meshyCfg = meshyTask ? MESHY_STATUS_LABELS[meshyTask.status] : null;
  const photoUrl = (item.photoOneDrivePath && item.photoUrl !== '#demo') ? `${IMG_BASE}/api/uploads/image?path=${encodeURIComponent(item.photoOneDrivePath)}` : null;

  return (
    <>
      <div onClick={() => setExpanded(!expanded)}
        style={{ display:'flex', alignItems:'center', gap:10, padding:`8px 16px 8px ${indent}px`,
          cursor:'pointer', borderTop:'0.5px solid #e5e7eb', background: depthBg(depth),
          filter: expanded ? 'brightness(0.97)' : 'none' }}
        onMouseEnter={e => e.currentTarget.style.filter='brightness(0.97)'}
        onMouseLeave={e => e.currentTarget.style.filter = expanded ? 'brightness(0.97)' : 'none'}>

        <span style={{ fontSize:14, color:'#9ca3af', flexShrink:0, userSelect:'none' }}>{expanded ? '▼' : '▶'}</span>
        <input type="checkbox" checked={!!selected} onClick={e => e.stopPropagation()} onChange={() => onToggleSelect(item.id)}
          style={{ accentColor:'var(--brand)', flexShrink:0 }} />

        {item.productImageUrl
          ? <LazyImage src={item.productImageUrl} alt="" style={{ width:28, height:28, objectFit:'contain', borderRadius:4, border:'0.5px solid #e5e7eb', flexShrink:0 }} />
          : <div style={{ width:28, height:28, flexShrink:0 }} />}

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:500, color:'#111827', display:'flex', alignItems:'center', gap:6 }}>
            {item.productName}
            {item.order?.isRush && <RushBadge />}
          </div>
          <div style={{ fontSize:11, color:'#6b7280', fontFamily:'monospace' }}>{item.lineItemDescription}</div>
        </div>

        {/* Phase 12 — model awaiting review from a PSP API submission */}
        {item.modelReviewStatus === 'pending' && (
          <span style={{ background:'#FEF3C7', color:'#92400E', padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:10, flexShrink:0, whiteSpace:'nowrap' }}>
            RAW review{item.modelReviewDeadlineAt ? ` · ${timeLeft(item.modelReviewDeadlineAt)}` : ''}
          </span>
        )}
        {Number(item.shipmentCount) > 1 && (
          <span title="Shipped to Solidiphy more than once (e.g. after rework)"
            style={{ background:'#E0F2FE', color:'#075985', padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:10, flexShrink:0, whiteSpace:'nowrap' }}>
            {item.shipmentCount} shipments
          </span>
        )}
        {item.shareMeshyWithPsp && (
          <span style={{ background:'#EDE9FE', color:'#6D28D9', padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:10, flexShrink:0, whiteSpace:'nowrap' }}>
            Meshy shared
          </span>
        )}
        {item.modelReviewStatus === 'on_hold' && (
          <span style={{ background:'#E5E7EB', color:'#374151', padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:10, flexShrink:0, whiteSpace:'nowrap' }}>
            RAW on hold
          </span>
        )}

        {/* Photo thumbnail — bigger (Phase 9), click to enlarge */}
        {photoUrl && (
          <LazyImage src={photoUrl} alt="Photo" onClick={e => { e.stopPropagation(); setRowLightbox(photoUrl); }}
            style={{ width:56, height:56, objectFit:'cover', borderRadius:6, border:'0.5px solid #e5e7eb', flexShrink:0, cursor:'zoom-in' }} />
        )}

        {/* Meshy status badge */}
        {meshyTask && meshyCfg && (
          <span style={{ background: meshyCfg.color + '22', color: meshyCfg.color,
            padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:10, flexShrink:0, whiteSpace:'nowrap' }}>
            🤖 {meshyCfg.label}
          </span>
        )}
        {item.meshyWarning && <span style={{ fontSize:10, flexShrink:0 }}>⚠️</span>}

        <div style={{ width:110, fontSize:11, flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          color: item.assignedPspCompany ? '#374151' : '#9ca3af' }}>
          {item.assignedPspCompany?.name || 'Unassigned'}
        </div>
        <div style={{ width:90, fontSize:11, color:'#6b7280', flexShrink:0 }}>{fmtDate(item.order?.createdAt)}</div>
        <div style={{ width:130, flexShrink:0 }}>
          <Badge status={item.itemStatus} />
          {item.itemStatus === 'sculpting' && item.stageDetail && (
            <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>{STAGE_LABEL[item.stageDetail]}</div>
          )}
        </div>

        <div style={{ width:200, flexShrink:0 }} onClick={e => e.stopPropagation()}>
          {isQC ? (
            <div style={{ display:'flex', gap:6 }}>
              <button className="btn btn-sm" onClick={() => handleQC('pass')}
                style={{ background:'#059669', color:'#fff', border:'none', fontSize:12, padding:'4px 10px' }}>✓ Pass</button>
              <button className="btn btn-sm" onClick={() => setShowQCFail(true)}
                style={{ background:'#DC2626', color:'#fff', border:'none', fontSize:12, padding:'4px 10px' }}>✗ Fail</button>
            </div>
          ) : availableStatuses.length > 0 ? (
            <select className='input' style={{ fontSize:12, padding:'4px 8px', width:'100%' }}
              value='' onChange={e => { if (e.target.value) handleStatusChange(e.target.value); }}>
              <option value=''>Change status…</option>
              {availableStatuses.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          ) : (
            <span style={{ fontSize:11, color:'#9ca3af', fontStyle:'italic' }}>No transitions</span>
          )}
        </div>
      </div>

      {expanded && (
        <OpsLineItemPanel
          item={item}
          toast={toast}
          onRefresh={onRefresh}
          meshyTask={meshyTask}
          onRetryMeshy={onRetryMeshy}
        />
      )}
      {rowLightbox && <Lightbox src={rowLightbox} onClose={() => setRowLightbox(null)} />}
      {showQCFail && (
        <QCFailModal onClose={() => setShowQCFail(false)} onSubmit={(reason) => handleQC('fail', reason)} />
      )}
    </>
  );
}, (prev, next) => {
  // Custom comparator (Phase 9 perf) — see PSPScreen's LineItemRow for the
  // same pattern and rationale: the refreshed `item` is a new object
  // reference every fetch even when nothing meaningful in it changed.
  const a = prev.item, b = next.item;
  return (
    a.id === b.id &&
    a.itemStatus === b.itemStatus &&
    a.photoOneDrivePath === b.photoOneDrivePath &&
    a.photoOneDrivePath2 === b.photoOneDrivePath2 &&
    a.photoUrl === b.photoUrl &&
    a.updatedAt === b.updatedAt &&
    a.order?.isRush === b.order?.isRush &&
    a.assignedPspCompanyId === b.assignedPspCompanyId &&
    a.modelReviewStatus === b.modelReviewStatus &&
    a.modelReviewDeadlineAt === b.modelReviewDeadlineAt &&
    a.stageDetail === b.stageDetail &&
    a.shareMeshyWithPsp === b.shareMeshyWithPsp &&
    a.shipmentCount === b.shipmentCount && a.returnReason === b.returnReason &&
    prev.selected === next.selected &&
    prev.depth === next.depth &&
    prev.meshyTask === next.meshyTask
  );
});

// ── Group/Order node ──────────────────────────────────────────────────────────
// Group-row checkbox (every grouping level): ticks/unticks every line item in
// the group; shows a dash when only some are selected.
function GroupCheckbox({ itemIds, selectedIds, onSetMany }) {
  const ref = React.useRef(null);
  const selCount = itemIds.reduce((n, id) => n + (selectedIds.has(id) ? 1 : 0), 0);
  const all = itemIds.length > 0 && selCount === itemIds.length;
  const some = selCount > 0 && !all;
  useEffect(() => { if (ref.current) ref.current.indeterminate = some; }, [some]);
  return (
    <input ref={ref} type="checkbox" checked={all} title={all ? 'Unselect all in this group' : 'Select all in this group'}
      onClick={e => e.stopPropagation()} onChange={() => onSetMany(itemIds, !all)}
      style={{ accentColor:'var(--brand)', flexShrink:0 }} />
  );
}

function OpsGroupNode({ label, sublabel, count, depth, children, itemIds, selectedIds, onSetMany, slipOrderId, toast }) {
  const [expanded, setExpanded] = useState(true);
  const indent = depth * 20;
  return (
    <>
      <div onClick={() => setExpanded(!expanded)}
        style={{ display:'flex', alignItems:'center', gap:10, padding:`10px 16px 10px ${16 + indent}px`,
          cursor:'pointer', borderTop:'0.5px solid #e5e7eb', background: depthBg(depth) }}
        onMouseEnter={e => e.currentTarget.style.filter='brightness(0.97)'}
        onMouseLeave={e => e.currentTarget.style.filter=''}>
        <span style={{ fontSize:14, color:'#9ca3af', flexShrink:0, userSelect:'none' }}>{expanded ? '▼' : '▶'}</span>
        {onSetMany ? <GroupCheckbox itemIds={itemIds} selectedIds={selectedIds} onSetMany={onSetMany} /> : <div style={{ width:13, flexShrink:0 }} />}
        <div style={{ width:28, flexShrink:0 }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:500, color:'#111827' }}>{label}</div>
          {sublabel && <div style={{ fontSize:11, color:'#6b7280' }}>{sublabel}</div>}
        </div>
        {slipOrderId && <OrderSlipsButton orderId={slipOrderId} toast={toast} />}
        <span style={{ fontSize:11, color:'#6b7280', flexShrink:0 }}>{count} item{count !== 1 ? 's' : ''}</span>
        <div style={{ width:110, flexShrink:0 }} />
        <div style={{ width:90, flexShrink:0 }} />
        <div style={{ width:130, flexShrink:0 }} />
        <div style={{ width:200, flexShrink:0 }} />
      </div>
      {expanded && children}
    </>
  );
}

// ── Grouped list renderer ─────────────────────────────────────────────────────
function OpsGroupedList({ items, groupLevels, depth = 0, toast, onRefresh, meshyTaskMap, onRetryMeshy, onOptimisticUpdate, selectedIds, onToggleSelect, onSetMany }) {
  if (!groupLevels.length) {
    return items.map(item => (
      <OpsLineItemRow key={item.id} item={item} depth={depth} toast={toast} onRefresh={onRefresh}
        meshyTask={meshyTaskMap[item.id]}
        onRetryMeshy={() => onRetryMeshy(item.id)} onOptimisticUpdate={onOptimisticUpdate}
        selected={selectedIds.has(item.id)} onToggleSelect={onToggleSelect} />
    ));
  }

  const [level, ...remaining] = groupLevels;
  const groups = {};
  for (const item of items) {
    const key = level === 'company' ? item.order?.companyName
      : level === 'group' ? item.order?.groupName
      : level === 'sku' ? (item.sku || 'No SKU')
      : level === 'order' ? item.order?.orderNumber
      : 'Other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }

  return Object.entries(groups).map(([key, groupItems]) => (
    <OpsGroupNode key={key} label={key} count={groupItems.length} depth={depth}
      sublabel={level === 'order' ? groupItems[0]?.order?.companyName + ' — ' + groupItems[0]?.order?.groupName : undefined}
      itemIds={groupItems.map(i => i.id)} selectedIds={selectedIds} onSetMany={onSetMany}
      slipOrderId={level === 'order' && groupItems.some(i => i.packingSlipId || Number(i.shipmentCount) > 0) ? groupItems[0].orderId : undefined}
      toast={toast}>
      <OpsGroupedList items={groupItems} groupLevels={remaining} depth={depth + 1}
        toast={toast} onRefresh={onRefresh} meshyTaskMap={meshyTaskMap} onRetryMeshy={onRetryMeshy} onOptimisticUpdate={onOptimisticUpdate}
        selectedIds={selectedIds} onToggleSelect={onToggleSelect} onSetMany={onSetMany} />
    </OpsGroupNode>
  ));
}

import ModelViewer from './ModelViewer';

const GROUP_OPTIONS = [
  { id:'company', label:'Company' },
  { id:'group',   label:'Group'   },
  { id:'sku',     label:'SKU'     },
  { id:'order',   label:'Order'   },
];

// ── Main OperationsTab component ──────────────────────────────────────────────

export default function OperationsTab() {
  const { toast } = useToast();
  const [items,          setItems]         = useState([]);
  const [loading,        setLoading]       = useState(true);
  const [search,         setSearch]        = useState('');
  const [statusFilter,   setStatusFilter]  = useState([]); // Phase 9 — multi-select
  const [companyFilter,  setCompanyFilter] = useState('');
  const [rushOnly,       setRushOnly]      = useState(false); // Phase 9
  const [hideShipped,    setHideShipped]   = useState(true); // on by default — same reasoning as the Orders tab
  const [meshyTaskMap,   setMeshyTaskMap]  = useState({});  // itemId → latest meshy task
  const [retryItem,      setRetryItem]     = useState(null); // itemId for retry modal
  const [retryTask,      setRetryTask]     = useState(null); // task for retry modal
  const [groupBy,        setGroupBy]       = useState(['company','group','order']);
  const [sortDir,        setSortDir]       = useState('desc');  // desc = newest first
  const [groupFilter,    setGroupFilter]   = useState('');
  // Phase 12 — PSP assignment
  const [pspCompanies,   setPspCompanies]  = useState([]);
  const [pspFilter,      setPspFilter]     = useState('');      // '' | 'unassigned' | companyId
  const [reviewOnly,     setReviewOnly]    = useState(false);   // pending model reviews only
  const [shipGroup,      setShipGroup]     = useState('');      // Phase 12c — PSP shipment group filter
  const [pspBatch,       setPspBatch]      = useState('');      // Phase 12e — PSP batch filter
  const [selectedIds,    setSelectedIds]   = useState(new Set());
  const [assignTarget,   setAssignTarget]  = useState('');
  const [assigning,      setAssigning]     = useState(false);

  useEffect(() => { getPSPCompanies().then(r => setPspCompanies(r.data || [])).catch(() => {}); }, []);
  // Group checkboxes: select / unselect a set of item ids at once
  const setManySelected = useCallback((ids, on) => setSelectedIds(prev => {
    const next = new Set(prev);
    ids.forEach(id => (on ? next.add(id) : next.delete(id)));
    return next;
  }), []);
  const toggleSelect = useCallback((id) => setSelectedIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  }), []);

  const toggleGroup = (id) => setGroupBy(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const orderedGroupBy = GROUP_OPTIONS.map(o => o.id).filter(id => groupBy.includes(id));

  // Phase 9 perf — instantly reflect a status change locally instead of
  // waiting on the PATCH + refetch round trip; onRefresh() still runs in the
  // background afterward to reconcile.
  const optimisticUpdate = useCallback((itemId, patch) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...patch } : i));
  }, []);

  // Derive unique companies and groups from loaded items for filter dropdowns
  const companies = [...new Set(items.map(i => i.order?.companyName).filter(Boolean))].sort();
  const groups    = [...new Set(items.filter(i => !companyFilter || i.order?.companyName === companyFilter).map(i => i.order?.groupName).filter(Boolean))].sort();

  const loadItems = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search)       params.search     = search;
    if (sortDir)      params.sortDir      = sortDir.toUpperCase();
    if (statusFilter.length) params.itemStatus = statusFilter.join(',');
    if (companyFilter) params.companyId = companyFilter;
    if (rushOnly)     params.rushOnly = 'true';
    if (hideShipped)  params.hideShipped = 'true';
    if (pspFilter)    params.pspCompanyId = pspFilter;
    if (shipGroup.trim()) params.shipmentGroupId = shipGroup.trim();
    if (pspBatch.trim())  params.pspBatchId = pspBatch.trim();

    getAdminLineItems(params)
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        setItems(data);
        // Load meshy tasks for these items
        loadMeshyTasks(data.map(i => i.id));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [search, statusFilter, sortDir, rushOnly, hideShipped, pspFilter, shipGroup, pspBatch]);

  const loadMeshyTasks = async (itemIds) => {
    if (!itemIds.length) return;
    try {
      const r = await getAdminMeshyTasks();
      const tasks = r.data.tasks || [];
      // Build map: itemId → most recent task
      const map = {};
      for (const task of tasks) {
        if (!map[task.orderItemId] || task.id > map[task.orderItemId].id) {
          map[task.orderItemId] = task;
        }
      }
      setMeshyTaskMap(map);
    } catch {}
  };

  useEffect(() => { loadItems(); }, [loadItems]);

  // Apply company and group filters client-side
  const filteredItems = items.filter(i => {
    if (companyFilter && i.order?.companyName !== companyFilter) return false;
    if (groupFilter   && i.order?.groupName   !== groupFilter)   return false;
    if (reviewOnly    && !['pending', 'on_hold'].includes(i.modelReviewStatus)) return false;
    return true;
  });
  // Phase 12b — review list: soonest deadline first (on hold / no deadline last)
  if (reviewOnly) {
    const key = (i) => i.modelReviewStatus === 'pending' && i.modelReviewDeadlineAt ? new Date(i.modelReviewDeadlineAt).getTime() : Infinity;
    filteredItems.sort((a, b) => key(a) - key(b));
  }

  // Selection only ever covers what's on screen — drop anything filtered away.
  const visibleSelected = filteredItems.filter(i => selectedIds.has(i.id)).map(i => i.id);
  const allVisibleSelected = filteredItems.length > 0 && visibleSelected.length === filteredItems.length;
  const toggleAllVisible = () => setSelectedIds(allVisibleSelected ? new Set() : new Set(filteredItems.map(i => i.id)));

  const handleAssign = async (targetId) => {
    const target = targetId === 'unassign' ? null : Number(targetId);
    const name = target ? (pspCompanies.find(c => c.id === target)?.name || 'PSP') : null;
    const n = visibleSelected.length;
    if (!window.confirm(target ? `Assign ${n} item${n !== 1 ? 's' : ''} to ${name}?` : `Unassign ${n} item${n !== 1 ? 's' : ''}? They will disappear from the PSP's queue.`)) return;
    setAssigning(true);
    try {
      const r = await assignItemsToPSP(visibleSelected, target);
      toast(target ? `${r.data.updated} item${r.data.updated !== 1 ? 's' : ''} assigned to ${name}` : `${r.data.updated} unassigned`, 'success');
      setSelectedIds(new Set()); setAssignTarget('');
      loadItems();
    } catch (err) { toast(err.response?.data?.error || 'Assignment failed', 'error'); }
    finally { setAssigning(false); }
  };

  const openRetry = (itemId) => {
    const task = meshyTaskMap[itemId];
    if (task) {
      setRetryItem(itemId);
      setRetryTask(task);
    } else {
      toast('No 3D task found for this item', 'error');
    }
  };



  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      <Toasts />

      {/* ── Toolbar ── */}
      <div style={{ padding:'12px 16px', background:'#fff', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <div style={{ fontSize:18, fontWeight:700, marginRight:8 }}>Operations Queue</div>

        <input className='input' placeholder='Search order, ref, SKU…' value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width:220, fontSize:13 }} />

        <StatusMultiSelect
          options={OPS_STATUSES.map(s => ({ value: s, label: STATUS_LABELS[s] }))}
          selected={statusFilter} onChange={setStatusFilter}
          placeholder="All statuses" width={200} />

        <select className='input' value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setGroupFilter(''); }}
          style={{ width:160, fontSize:13 }}>
          <option value=''>All companies</option>
          {companies.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select className='input' value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
          style={{ width:150, fontSize:13 }}>
          <option value=''>All groups</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>

        <select className='input' value={pspFilter} onChange={e => { setPspFilter(e.target.value); setSelectedIds(new Set()); }}
          style={{ width:150, fontSize:13 }}>
          <option value=''>All PSPs</option>
          <option value='unassigned'>Unassigned</option>
          {pspCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <input className='input' value={shipGroup} onChange={e => setShipGroup(e.target.value)}
          placeholder='Shipment group' title='PSP shipment group ID (exact match)' style={{ width:140, fontSize:13 }} />
        <input className='input' value={pspBatch} onChange={e => setPspBatch(e.target.value)}
          placeholder='PSP batch' title='PSP batch ID (exact match)' style={{ width:120, fontSize:13 }} />

        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer' }}>
          <input type='checkbox' checked={reviewOnly} onChange={e => setReviewOnly(e.target.checked)} style={{ accentColor:'var(--brand)' }} />
          Model review
        </label>

        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer' }}>
          <input type='checkbox' checked={rushOnly} onChange={e => setRushOnly(e.target.checked)} style={{ accentColor:'var(--brand)' }} />
          🔥 Rush only
        </label>

        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, cursor:'pointer' }}>
          <input type='checkbox' checked={hideShipped} onChange={e => setHideShipped(e.target.checked)} style={{ accentColor:'var(--brand)' }} disabled={statusFilter.length > 0} />
          Hide shipped
        </label>

        <select className='input' value={sortDir} onChange={e => setSortDir(e.target.value)}
          style={{ width:160, fontSize:13 }}>
          <option value='asc'>Oldest first</option>
          <option value='desc'>Newest first</option>
        </select>

        <button className='btn' onClick={loadItems}>Refresh</button>

        <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:8 }}>
          <span style={{ fontSize:12, color:'#6b7280' }}>Group by:</span>
          {GROUP_OPTIONS.map(opt => (
            <label key={opt.id} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, cursor:'pointer' }}>
              <input type='checkbox' checked={groupBy.includes(opt.id)} onChange={() => toggleGroup(opt.id)}
                style={{ accentColor:'var(--brand)' }} />
              {opt.label}
            </label>
          ))}
        </div>

        <span style={{ fontSize:12, color:'#9ca3af', marginLeft:'auto' }}>
          {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Phase 12: bulk assignment bar ── */}
      {visibleSelected.length > 0 && (
        <div style={{ padding:'8px 16px', background:'#EEF2FF', borderBottom:'1px solid #c7d2fe', display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:13, fontWeight:500 }}>{visibleSelected.length} selected</span>
          <select className='input' value={assignTarget} onChange={e => setAssignTarget(e.target.value)} style={{ width:200, fontSize:13 }}>
            <option value=''>Assign to PSP…</option>
            {pspCompanies.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            <option value='unassign'>— Unassign —</option>
          </select>
          <button className='btn btn-primary btn-sm' disabled={!assignTarget || assigning} onClick={() => handleAssign(assignTarget)}>
            {assigning ? 'Applying…' : 'Apply'}
          </button>
          <button className='btn btn-sm' onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      {/* ── Table header ── */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 16px',
        background:'#f0f4f8', borderBottom:'0.5px solid #e5e7eb', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ width:14, flexShrink:0 }} />
        <input type='checkbox' checked={allVisibleSelected} onChange={toggleAllVisible} title='Select all shown'
          style={{ accentColor:'var(--brand)', flexShrink:0 }} />
        <div style={{ width:28, flexShrink:0 }} />
        <div style={{ flex:1, fontSize:11, fontWeight:500, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em' }}>Item / Order</div>
        <div style={{ width:32, flexShrink:0 }} />{/* photo thumb */}
        <div style={{ width:110, fontSize:11, fontWeight:500, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>3D Status</div>
        <div style={{ width:110, fontSize:11, fontWeight:500, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>PSP</div>
        <div style={{ width:90, fontSize:11, fontWeight:500, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>Order Date</div>
        <div style={{ width:130, fontSize:11, fontWeight:500, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>Status</div>
        <div style={{ width:200, fontSize:11, fontWeight:500, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>Actions</div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex:1, overflowY:'auto' }}>
        {loading && items.length === 0 ? (
          <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>
        ) : filteredItems.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>No items found.</div>
        ) : (
          <OpsGroupedList items={filteredItems} groupLevels={orderedGroupBy} depth={0}
            toast={toast} onRefresh={loadItems}
            meshyTaskMap={meshyTaskMap} onRetryMeshy={openRetry} onOptimisticUpdate={optimisticUpdate}
            selectedIds={selectedIds} onToggleSelect={toggleSelect} onSetMany={setManySelected} />
        )}
      </div>

      {/* ── Meshy Retry Modal ── */}
      {retryTask && (
        <MeshyRetryModal
          task={retryTask}
          onClose={() => { setRetryTask(null); setRetryItem(null); }}
          onSuccess={loadItems}
          toast={toast}
        />
      )}
    </div>
  );
}
